import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
  Button, Card, Col, Container, Form, InputGroup, Row, Table, Badge,
} from "react-bootstrap";
import debounce from "lodash/debounce";
import { useReactToPrint } from "react-to-print";
import { InspectionsClient } from "../web-api-client.ts";
import QR from "./QR.js";
import { QRCodeCanvas } from "qrcode.react";
import UserContext from "../providers/UserContext.js";


/** Print-safe QR:
 *  - Screen: your fancy <QR /> (SVG + headline/arrow)
 *  - Print: high-res PNG from a hidden <canvas> (never gets cropped)
 */

/**
 * Composes a single PNG (QR + curved red arrow outside the QR).
 * - Keeps the QR itself at `qrInches` width on paper.
 * - Adds extra canvas width on the right so the arrow never overlaps the QR.
 */
const PrintSafeQR = ({ url, qrInches = 2.75 }) => {
  // --- High-res QR to rasterize ---
  const QR_SIZE = 400;     // px
  const MARGIN  = 40;       // px padding around QR inside composite
  const EXTRA_RIGHT = 380;  // px reserved to the right for the arrow (tune if needed)

  // Arrow styling in the high-res coordinate space
  const ARROW_COLOR = "#C65A43";
  const ARROW_WIDTH = 28;   // stroke width in px (at QR_SIZE res)
  const ARROW_HEAD  = 44;   // head size in px (at QR_SIZE res)

  const qrCanvasRef = useRef(null);
  const [pngSrc, setPngSrc] = useState("");

  useEffect(() => {
    let raf = 0;

    const compose = () => {
      const qr = qrCanvasRef.current;
      if (!qr) return;

      // Composite canvas size (wider than QR to the right)
      const W = MARGIN + QR_SIZE + EXTRA_RIGHT + MARGIN;
      const H = MARGIN + QR_SIZE + MARGIN;

      const comp = document.createElement("canvas");
      comp.width = W;
      comp.height = H;
      const ctx = comp.getContext("2d");

      // White background (keeps print consistent)
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, W, H);

      // Draw QR anchored at left with margin
      const qx = MARGIN;
      const qy = MARGIN;
      ctx.drawImage(qr, qx, qy);

      // ===== Curved arrow outside the QR (right side) =====
      // Tip just OUTSIDE the QR's top-right corner
      const endX = qx + QR_SIZE + 18;                // 18px beyond QR edge
      const endY = qy + QR_SIZE * 0.40;              // around upper half

      // Start further to the right and slightly higher
      const startX = qx + QR_SIZE + EXTRA_RIGHT - 10;
      const startY = qy + QR_SIZE * 0.22;

      // Control points to arc outward then back to the tip
      const c1x = startX + 20;
      const c1y = startY - 160;
      const c2x = endX + 160;
      const c2y = endY - 60;

      ctx.lineWidth = ARROW_WIDTH;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = ARROW_COLOR;

      ctx.beginPath();
      ctx.moveTo(startX, startY);
      ctx.bezierCurveTo(c1x, c1y, c2x, c2y, endX, endY);
      ctx.stroke();

      // Arrowhead aligned with curve tangent
      const vx = endX - c2x;
      const vy = endY - c2y;
      const vlen = Math.hypot(vx, vy) || 1;
      const ux = vx / vlen;
      const uy = vy / vlen;
      const baseX = endX - ux * (ARROW_HEAD + 8);
      const baseY = endY - uy * (ARROW_HEAD + 8);
      const px = -uy, py = ux;

      const leftX  = baseX + px * (ARROW_HEAD * 0.65);
      const leftY  = baseY + py * (ARROW_HEAD * 0.65);
      const rightX = baseX - px * (ARROW_HEAD * 0.65);
      const rightY = baseY - py * (ARROW_HEAD * 0.65);

      ctx.fillStyle = ARROW_COLOR;
      ctx.beginPath();
      ctx.moveTo(endX, endY);
      ctx.lineTo(leftX, leftY);
      ctx.lineTo(rightX, rightY);
      ctx.closePath();
      ctx.fill();

      // Export composite PNG
      try {
        setPngSrc(comp.toDataURL("image/png"));
      } catch {
        setPngSrc("");
      }
    };

    // Wait a frame so QRCodeCanvas draws
    raf = requestAnimationFrame(compose);
    return () => cancelAnimationFrame(raf);
  }, [url]);

  // —— Keep the QR itself at qrInches wide on paper ——
  // The composite image is wider than the QR by EXTRA_RIGHT + margins.
  // We scale the <img> so that the QR region ends up == qrInches.
  const COMPOSITE_W = MARGIN + QR_SIZE + EXTRA_RIGHT + MARGIN;
  const qrToCompositeRatio = QR_SIZE / COMPOSITE_W;
  const imgInches = qrInches / qrToCompositeRatio;    // final <img> width so QR == qrInches

  return (
    <>
      {/* PRINT-ONLY: single robust image (QR + arrow) */}
      <div className="print-only" style={{ textAlign: "center" }}>
       
          <img
            src="scanme.png"
            alt="scan me with arrow"
            style={{
              width: `7in`,   // ensures the QR itself remains qrInches wide
              maxWidth: "100%",
              height: "auto",
              display: "block",
              margin: "0 auto",
            }}
          />
        <div style={{ marginTop: "-150px" }}>
          <QRCodeCanvas ref={qrCanvasRef} value={url} size={QR_SIZE} />
        </div>

         <img
            src="logo-dark.png"
            alt="qcar logo"
            style={{
              width: `4in`,   // ensures the QR itself remains qrInches wide
              maxWidth: "100%",
              height: "auto",
              display: "block",
              margin: "50px auto",
            }}
          />
      </div>

      {/* Hidden: high-res QR canvas used to generate the PNG */}
      <div style={{ position: "absolute", left: -99999 }} className="screen-only" aria-hidden="true">
       
      </div>
    </>
  );
};




const QrBatchPrint = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [inspections, setInspections] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [error, setError] = useState(null);
  const user = React.useContext(UserContext);

  const inspectionsClient = useMemo(() => new InspectionsClient(), []);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    let isActive = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const list = await inspectionsClient.getInspectionList(user.dealerId, 1, 25, undefined, undefined);
        if (!isActive) return;
        const normalized = (list.items || []).map(x => ({
          id: x.id,
          vehicleLabel: x.vehicleLabel || "",
          vehicleYear: x.vehicleYear || "",
          vehicleMake: x.vehicleMake || "",
          vehicleModel: x.vehicleModel || "",
          vehicleStockNumber: x.vehicleStockNumber || "",
          vehicleVin: x.vehicleVin || x.vehicleLabel || "",
          status: x.status,
          inspectionDate: x.inspectionDate ? new Date(x.inspectionDate) : null,
          inspectorName: x.inspectorName || "",
        }));
        setInspections(normalized);
      } catch (e) {
        console.error(e);
        if (isActive) setError("Failed to load inspections.");
      } finally {
        if (isActive) setLoading(false);
      }
    };
    load();
    return () => { isActive = false; };
  }, [inspectionsClient]);

  const applyFilter = useCallback(() => {
    const s = search.trim().toLowerCase();
    let data = inspections;

    if (statusFilter !== "all") {
      const sf = Number(statusFilter);
      data = data.filter(d => d.status === sf);
    }

    if (s) {
      
      data = data.filter(d => {
        const parts = [
          d.id?.toString() || "",
          d.vehicleVin || "",
          d.vehicleLabel || "",
          d.vehicleYear?.toString() || "",
          d.vehicleMake || "",
          d.vehicleModel || "",
          d.vehicleStockNumber || "",
          d.inspectorName || "",
          d.inspectionDate ? d.inspectionDate.toLocaleDateString() : "",
        ].map(v => (v || "").toLowerCase());
        return parts.some(p => p.includes(s));
      });
    }

    setFiltered(data);
  }, [inspections, search, statusFilter]);

  const debouncedFilter = useMemo(() => debounce(applyFilter, 200), [applyFilter]);

  useEffect(() => {
    debouncedFilter();
    return () => debouncedFilter.cancel();
  }, [debouncedFilter, search, statusFilter, inspections]);

  const toggleOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllOnPage = () => {
    const allSelected = filtered.every(x => selectedIds.has(x.id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allSelected) filtered.forEach(x => next.delete(x.id));
      else filtered.forEach(x => next.add(x.id));
      return next;
    });
  };

  const clearAll = () => setSelectedIds(new Set());
  const canProceed = selectedIds.size > 0;

  const goNext = () => setStep(2);
  const goBack = () => setStep(1);

  const statusBadge = (status) => {
    switch (status) {
      case 0: return <Badge bg="success">Pass</Badge>;
      case 1: return <Badge bg="warning" text="dark">Attention</Badge>;
      case 2: return <Badge bg="danger">Fail</Badge>;
      default: return <Badge bg="secondary">Unknown</Badge>;
    }
  };

  const selectedList = useMemo(
    () => inspections.filter(x => selectedIds.has(x.id)),
    [inspections, selectedIds]
  );

  const qrUrlFor = (id) => `${window.location.origin}/dvi/view/${id}`;

  // ===== Print CSS (no fixed page shell; robust sizes; clear toggles) =====
  const CRITICAL_PRINT_CSS = `
    @page { size: Letter portrait; margin: 0.5in; }

    html, body {
      margin: 0 !important;
      padding: 0 !important;
      background: #fff !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      font-family: "Segoe UI", Arial, Helvetica, sans-serif !important;
    }
    *, *::before, *::after { box-sizing: border-box; }

    .no-print { display: none !important; }
    .print-page { break-after: page; page-break-after: always; }

    /* Remove preview chrome in print */
    .preview-stack { display: block !important; }
    .page-shadow { all: unset; display: block; }

    /* Fill printable area (page minus margins) and center content */
    .qr-fullpage {
      width: 100% !important;
      min-height: calc(11in - 1in) !important;
      padding: 0 !important;
      display: flex !important;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
      color: #000 !important;
     
     
    }

    .qr-box { width: 100% !important; display: grid !important; place-items: center; }

    /* Show print-only, hide screen-only in print */
    .screen-only { display: none !important; }
    .print-only  { display: block !important; }

    .qr-meta { margin-top: 8pt; font-size: 12pt; line-height: 1.25; color: #000 !important; }
    .qr-title { font-weight: 700; font-size: 13pt; color: #000 !important; }
  `;

  // ===== react-to-print (v3) =====
  const printAreaRef = useRef(null);
  const handlePrint = useReactToPrint({
    contentRef: printAreaRef,
    documentTitle: "QCar - Inspection QRs",
    removeAfterPrint: true,
    copyStyles: true,
    pageStyle: CRITICAL_PRINT_CSS,
    onBeforeGetContent: () =>
      new Promise((resolve) => requestAnimationFrame(resolve)), // ensure PNG is ready
  });

  return (
    <Container fluid className="py-3">
      {/* Screen-only styling for the preview */}
      <style>{`
        .preview-stack { display: grid; gap: 24px; }
        .page-shadow {
          background: white;
          margin: 0 auto;
          width: 8.5in;
          max-width: 95vw;
          aspect-ratio: 8.5 / 11;
          box-shadow: 0 6px 24px rgba(0,0,0,0.18);
          border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
        }
        .qr-fullpage {
          width: 100%;
          height: 100%;
          padding: 0.5in;
          display: flex; flex-direction: column;
          align-items: center; justify-content: center;
          text-align: center; color: #000 !important;
        }
        .qr-meta { margin-top: 12px; font-size: 1rem; line-height: 1.2; word-break: break-word; color: #000 !important; }
        .qr-title { font-weight: 700; font-size: 1.1rem; color: #000 !important; }
      `}</style>

      {step === 1 && (
        <>
          <Row className="mb-3">
            <Col><h3 className="mb-0">Print QR Codes – Select Inspections</h3></Col>
          </Row>

          <Card className="mb-3">
            <Card.Body>
              <Row className="g-2 align-items-end">
                <Col md={4}>
                  <Form.Label className="mb-1">Search</Form.Label>
                  <InputGroup>
                    <Form.Control
                      placeholder="VIN, Make Model, Stock #..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                    {search && (
                      <Button variant="outline-secondary" onClick={() => setSearch("")}>
                        Clear
                      </Button>
                    )}
                  </InputGroup>
                </Col>
               {/*  <Col md={3}>
                  <Form.Label className="mb-1">Status</Form.Label>
                  <Form.Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="all">All</option>
                    <option value="0">Pass</option>
                    <option value="1">Attention</option>
                    <option value="2">Fail</option>
                  </Form.Select>
                </Col> */}
                <Col md="auto" className="ms-auto">
                  <div className="text-end">
                    <div className="small text-muted">Selected: {selectedIds.size}</div>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="mt-2"
                      onClick={clearAll}
                      disabled={selectedIds.size === 0}
                    >
                      Clear Selected
                    </Button>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>Inspections</div>
              <div className="no-print">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={toggleAllOnPage}
                  disabled={filtered.length === 0}
                >
                  {filtered.length > 0 && filtered.every(x => selectedIds.has(x.id))
                    ? "Unselect All"
                    : "Select All"}
                </Button>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {loading && <div className="p-3 text-muted">Loading inspections…</div>}
              {error && <div className="p-3 text-danger">{error}</div>}
              {!loading && !error && filtered.length === 0 && (
                <div className="p-3 text-muted">No inspections found.</div>
              )}
              {!loading && !error && filtered.length > 0 && (
                <div className="table-responsive">
                  <Table hover className="mb-0 align-middle">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: 48 }}></th>
                        <th>Year</th>
                        <th>Make</th>
                        <th>Model</th>
                        <th>Stock Number</th>
                        <th>Vin</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map(row => (
                        <tr key={row.id} onClick={() => toggleOne(row.id)} style={{ cursor: "pointer" }}>
                          <td>
                            <Form.Check
                              type="checkbox"
                              checked={selectedIds.has(row.id)}
                              onChange={() => toggleOne(row.id)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </td>
                          <td>{row.vehicleYear}</td>
                          <td>{row.vehicleMake || "—"}</td>
                          <td>{row.vehicleModel || "—"}</td>
                          <td>{row.vehicleStockNumber || "—"}</td>
                          <td>{row.vehicleVin || "—"}</td>
                          <td>{row.inspectionDate ? row.inspectionDate.toLocaleString() : "—"}</td>
                          
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
            <Card.Footer className="d-flex justify-content-end gap-2 no-print">
              <Button variant="primary" disabled={!canProceed} onClick={goNext}>
                Next: Full-Page Preview
              </Button>
            </Card.Footer>
          </Card>
        </>
      )}

      {step === 2 && (
        <>
          <Row className="no-print mb-3">
            <Col className="d-flex justify-content-between align-items-center">
              <h3 className="mb-0">Full-Page QR Preview</h3>
              <div className="d-flex gap-2">
                <Button variant="outline-secondary" onClick={goBack}>Back</Button>
                <Button variant="primary" onClick={handlePrint} disabled={selectedList.length === 0}>
                  Print
                </Button>
              </div>
            </Col>
          </Row>

          {/* Only this area will be printed */}
          <div ref={printAreaRef}>
            {/* Ensure the print iframe gets the critical CSS */}
            <style media="print">{CRITICAL_PRINT_CSS}</style>

            <div className="preview-stack">
              {selectedList.map(item => {
                const url = qrUrlFor(item.id);
                return (
                  <div className="page-shadow print-page" key={item.id}>
                    <div className="qr-fullpage">
                      <div className="qr-box">
                        <PrintSafeQR url={url} />
                      </div>
                      <div className="qr-meta">
                        <div className="qr-title">{item.vehicleLabel || `Inspection #${item.id}`}</div>
                        <div>{item.vehicleVin || "—"}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="no-print d-flex justify-content-end mt-3">
            <Button variant="primary" onClick={handlePrint} disabled={selectedList.length === 0}>
              Print
            </Button>
          </div>
        </>
      )}
    </Container>
  );
};

export default QrBatchPrint;
